import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { MobileStickyCTA } from "@/components/MobileStickyCTA";
import { buildLocalBusiness, buildOrganization, buildWebSite } from "@/components/SchemaJsonLd";
import { captureAttribution } from "@/lib/attribution";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <p className="text-xs uppercase tracking-[0.3em] text-copper">404</p>
        <h1 className="mt-3 text-4xl font-display uppercase text-silver-light">Page not found</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          The page you're looking for doesn't exist. Let's get you back on track.
        </p>
        <div className="mt-6 flex justify-center gap-2">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-sm bg-copper px-5 py-3 text-xs font-bold uppercase tracking-wider text-white hover:bg-copper-hover"
          >
            Go Home
          </Link>
          <Link
            to="/quote"
            className="inline-flex items-center justify-center rounded-sm border border-border px-5 py-3 text-xs font-bold uppercase tracking-wider text-silver-light hover:border-copper hover:text-copper"
          >
            Get a Quote
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold text-silver-light">This page didn't load</h1>
        <p className="mt-2 text-sm text-muted-foreground">Something went wrong. Try again or head back home.</p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => { router.invalidate(); reset(); }}
            className="inline-flex items-center justify-center rounded-sm bg-copper px-5 py-3 text-xs font-bold uppercase tracking-wider text-white"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-sm border border-border px-5 py-3 text-xs font-bold uppercase tracking-wider text-silver-light"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "theme-color", content: "#0B0B0B" },
      { title: "MLC Epoxy Floors | Premium Epoxy Floors in Calgary" },
      { name: "description", content: "Premium garage floor coatings and concrete coating systems serving Calgary and surrounding areas." },
      { name: "author", content: "MLC Epoxy Floors" },
      { property: "og:site_name", content: "MLC Epoxy Floors" },
      { property: "og:type", content: "website" },
      { property: "og:title", content: "MLC Epoxy Floors | Premium Epoxy Floors in Calgary" },
      { property: "og:description", content: "Premium garage floor coatings and concrete coating systems serving Calgary and surrounding areas." },
      { property: "og:locale", content: "en_CA" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "MLC Epoxy Floors | Premium Epoxy Floors in Calgary" },
      { name: "twitter:description", content: "Premium garage floor coatings and concrete coating systems serving Calgary and surrounding areas." },
      { property: "og:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/bdc2ecc0-6af7-499f-a944-14c8e4bacec5" },
      { name: "twitter:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/bdc2ecc0-6af7-499f-a944-14c8e4bacec5" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
    ],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(buildOrganization()) },
      { type: "application/ld+json", children: JSON.stringify(buildWebSite()) },
      { type: "application/ld+json", children: JSON.stringify(buildLocalBusiness()) },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  const fontHref = "https://fonts.googleapis.com/css2?family=Archivo+Black&family=Inter:wght@400;500;600;700;800&display=swap";
  return (
    <html lang="en">
      <head>
        <meta name="google-site-verification" content="MxphncdEAEr3r5nBfiAg9zYVVGY_JBp_i5VsT_uNXYY" />
        {/* Non-blocking Google Fonts */}
        <link rel="preload" as="style" href={fontHref} />
        <link
          rel="stylesheet"
          href={fontHref}
          media="print"
          // @ts-expect-error onload string is valid in raw HTML
          onLoad="this.media='all'"
        />
        <noscript>
          <link rel="stylesheet" href={fontHref} />
        </noscript>
        <HeadContent />
      </head>
      <body>
        {/* Deferred third-party: GTM + scalecro tracking, loaded after page load */}
        <script
          dangerouslySetInnerHTML={{
            __html: `(function(){function load(){var s=document.createElement('script');s.async=true;s.src='https://link.scalecro.com/js/external-tracking.js';s.setAttribute('data-tracking-id','tk_95e68915e2df45328fdf92c0b79dcfb7');document.body.appendChild(s);(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','GTM-K74W858G');}if(document.readyState==='complete'){setTimeout(load,1200);}else{window.addEventListener('load',function(){setTimeout(load,1200);});}})();`,
          }}
        />
        <noscript>
          <iframe
            src="https://www.googletagmanager.com/ns.html?id=GTM-K74W858G"
            height="0"
            width="0"
            style={{ display: "none", visibility: "hidden" }}
          />
        </noscript>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  useEffect(() => {
    captureAttribution();
  }, []);
  return (
    <QueryClientProvider client={queryClient}>
      <Header />
      <main className="min-h-[60vh] pb-24 lg:pb-0">
        <Outlet />
      </main>
      <Footer />
      <MobileStickyCTA />
    </QueryClientProvider>
  );
}
